import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity as ActivityIcon,
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Filter,
  RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useRealtime } from "@/lib/realtime";

const SEVERITY_TONE: Record<string, string> = {
  urgent: "text-rose-600 bg-rose-50 ring-rose-200",
  warn: "text-amber-700 bg-amber-50 ring-amber-200",
  info: "text-zinc-700 bg-zinc-50 ring-zinc-200",
};

const SEVERITY_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  urgent: AlertOctagon,
  warn: AlertTriangle,
  info: CheckCircle2,
};

interface FeedItem {
  id: string;
  service: string;
  event_type: string;
  label: string;
  severity: string;
  occurred_at: string | null;
  cost_cents: number;
  duration_ms: number | null;
  trace_id: string | null;
  payload: Record<string, unknown>;
  preview: string;
}

export function Activity() {
  const [severityFilter, setSeverityFilter] = React.useState<string>("");
  const [search, setSearch] = React.useState("");
  const q = useQuery({
    queryKey: ["activity", severityFilter],
    queryFn: () =>
      api.activityFeed({
        limit: 200,
      }),
    refetchInterval: 8_000,
  });
  // bump on any new event row
  useRealtime("events", () => q.refetch());

  const items: FeedItem[] = (q.data ?? []) as FeedItem[];
  const filtered = React.useMemo(() => {
    let f = items;
    if (severityFilter) f = f.filter((i) => i.severity === severityFilter);
    if (search) {
      const s = search.toLowerCase();
      f = f.filter(
        (i) =>
          i.label.toLowerCase().includes(s) ||
          i.preview.toLowerCase().includes(s) ||
          i.service.toLowerCase().includes(s) ||
          i.event_type.toLowerCase().includes(s),
      );
    }
    return f;
  }, [items, severityFilter, search]);

  // Bucket by hour for visual grouping
  const buckets = React.useMemo(() => groupByHour(filtered), [filtered]);

  // Costs / counts summary at top
  const summary = React.useMemo(() => {
    const totalCost = filtered.reduce((acc, i) => acc + (i.cost_cents || 0), 0);
    const errors = filtered.filter((i) => i.severity === "urgent").length;
    const warns = filtered.filter((i) => i.severity === "warn").length;
    return { totalCost, errors, warns, total: filtered.length };
  }, [filtered]);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ActivityIcon className="h-5 w-5" /> Activity
          </h1>
          <p className="text-sm text-muted-foreground">
            Live feed of everything Kitt is doing — sends, scores, drops, errors. Last 24 hours.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
        >
          <RefreshCw className={"mr-2 h-4 w-4 " + (q.isFetching ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Events (24h)" value={String(summary.total)} />
        <SummaryCard label="Errors" value={String(summary.errors)} tone={summary.errors > 0 ? "danger" : "neutral"} />
        <SummaryCard label="Warnings" value={String(summary.warns)} tone={summary.warns > 0 ? "warn" : "neutral"} />
        <SummaryCard label="Spend" value={`$${(summary.totalCost / 100).toFixed(2)}`} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 rounded-md border bg-card px-3 py-2">
        <Filter className="h-3.5 w-3.5 text-muted-foreground" />
        <Input
          placeholder="search events..."
          className="h-8 max-w-xs"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex gap-1">
          {["", "urgent", "warn", "info"].map((s) => (
            <Button
              key={s || "all"}
              size="sm"
              variant={severityFilter === s ? "default" : "outline"}
              onClick={() => setSeverityFilter(s)}
            >
              {s || "all"}
            </Button>
          ))}
        </div>
      </div>

      {/* Feed */}
      {q.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Empty
          title="Nothing in the last 24h matches"
          hint="Try clearing filters, or check System for queue/health."
        />
      ) : (
        <div className="space-y-3">
          {buckets.map(([hour, rows]) => (
            <section key={hour}>
              <div className="text-2xs uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {hour} · {rows.length} event{rows.length === 1 ? "" : "s"}
              </div>
              <div className="space-y-1">
                {rows.map((r) => <ActivityRow key={r.id} row={r} />)}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ row }: { row: FeedItem }) {
  const Icon = SEVERITY_ICON[row.severity] ?? CheckCircle2;
  const tone = SEVERITY_TONE[row.severity] ?? SEVERITY_TONE.info;
  const time = row.occurred_at ? row.occurred_at.slice(11, 19) : "";
  return (
    <div className="flex items-start gap-3 rounded-md border bg-card px-3 py-2 hover:bg-muted/30 text-sm">
      <div className={`rounded-full p-1 ring-1 ring-inset ${tone}`}>
        <Icon className="h-3 w-3" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="font-medium">{row.label}</span>
          <span className="text-2xs font-mono text-muted-foreground">{row.service}</span>
          {row.cost_cents > 0 && (
            <span className="text-2xs text-muted-foreground">
              ${(row.cost_cents / 100).toFixed(4)}
            </span>
          )}
          {row.duration_ms && (
            <span className="text-2xs text-muted-foreground">
              {row.duration_ms}ms
            </span>
          )}
        </div>
        {row.preview && (
          <div className="text-2xs text-muted-foreground truncate mt-0.5">
            {row.preview}
          </div>
        )}
      </div>
      <span className="text-2xs font-mono text-muted-foreground whitespace-nowrap pt-0.5">
        {time}
      </span>
    </div>
  );
}

function SummaryCard({ label, value, tone = "neutral" }: { label: string; value: string; tone?: "neutral" | "warn" | "danger" }) {
  const accent =
    tone === "danger" ? "text-rose-600" : tone === "warn" ? "text-amber-700" : "";
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-2xs uppercase text-muted-foreground">{label}</div>
      <div className={`text-xl font-semibold ${accent}`}>{value}</div>
    </div>
  );
}

function groupByHour(items: FeedItem[]): [string, FeedItem[]][] {
  const map = new Map<string, FeedItem[]>();
  for (const i of items) {
    if (!i.occurred_at) continue;
    const d = new Date(i.occurred_at);
    const hour = d.toLocaleString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    // Bucket to the hour
    const bucket = d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      hour12: true,
    });
    const key = bucket.replace(/:.+/, ""); // strip minutes
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(i);
  }
  return Array.from(map.entries());
}
